import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import ExpertAvatarUpload from '@/components/ExpertAvatarUpload';
import { useCreateExpertProfile } from '@/hooks/useCreateExpertProfile';
import type { ExpertProfileFormData, ExpertProfileFormErrors, UploadedAvatar } from '@/types/expert-profile';

const initialFormData: ExpertProfileFormData = {
  name: '',
  subtitle: '',
  avatar: null,
  avatarFileId: null,
};

function validateForm(data: ExpertProfileFormData): ExpertProfileFormErrors {
  const errors: ExpertProfileFormErrors = {};

  if (!data.name.trim()) errors.name = 'Name is required';
  else if (data.name.length > 255) errors.name = 'Name must be 255 characters or less';

  if (!data.subtitle.trim()) errors.subtitle = 'Subtitle is required';
  else if (data.subtitle.length > 255) errors.subtitle = 'Subtitle must be 255 characters or less';

  return errors;
}

export default function NewExpertProfilePage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ExpertProfileFormData>(initialFormData);
  const [errors, setErrors] = useState<ExpertProfileFormErrors>({});
  const createMutation = useCreateExpertProfile();

  useEffect(() => {
    document.title = 'New Expert Profile | Admin Dashboard';
    return () => {
      document.title = 'Admin Dashboard';
    };
  }, []);

  const handleFieldChange = (field: keyof ExpertProfileFormData, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof ExpertProfileFormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleAvatarChange = (avatar: UploadedAvatar | null) => {
    setFormData((prev) => ({
      ...prev,
      avatar,
      avatarFileId: avatar?.documentId ?? null,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error('Please fix the form errors');
      return;
    }

    try {
      await createMutation.mutateAsync(formData);
      navigate('/admin/expert-profiles');
    } catch (error) {
      console.error('Failed to create expert profile:', error);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Back button */}
      <button
        onClick={() => navigate('/admin/expert-profiles')}
        className="flex items-center gap-2 text-pine-ink-soft hover:text-pine-ink transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>Back to Expert Profiles</span>
      </button>

      <h1 className="text-pine-display font-display text-pine-ink">New Expert Profile</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info Card */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">
                Name <span className="text-red-400">*</span>
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                placeholder="e.g. Dr. Sarah Johnson"
              />
              {errors.name && <p className="text-sm text-red-400">{errors.name}</p>}
            </div>

            {/* Subtitle */}
            <div className="space-y-2">
              <Label htmlFor="subtitle">
                Subtitle <span className="text-red-400">*</span>
              </Label>
              <Input
                id="subtitle"
                value={formData.subtitle}
                onChange={(e) => handleFieldChange('subtitle', e.target.value)}
                placeholder="e.g. Child Psychologist & Play Specialist"
              />
              {errors.subtitle && <p className="text-sm text-red-400">{errors.subtitle}</p>}
            </div>
          </CardContent>
        </Card>

        {/* Avatar Card */}
        <Card>
          <CardHeader>
            <CardTitle>Avatar</CardTitle>
          </CardHeader>
          <CardContent>
            <ExpertAvatarUpload
              value={formData.avatar}
              onChange={handleAvatarChange}
            />
            {errors.avatar && <p className="text-sm text-red-400 mt-2">{errors.avatar}</p>}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="admin-button-primary"
          >
            {createMutation.isPending ? 'Creating...' : 'Create Expert Profile'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/expert-profiles')}
            className="px-6 py-2.5 rounded-lg border border-pine-line text-pine-ink-soft hover:bg-pine-hover-button transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
